import { TeamRole } from "@vv/teams-frontend";

export enum RequestStatus {
  Submitted = "Submitted",
  Assigned = "Assigned",
  Rejected = "Rejected",
  InProgress = "InProgress",
  Completed = "Completed",
  ReworkRequired = "ReworkRequired",
}

export enum Priority {
  Critical = "Critical",
  High = "High",
  Medium = "Medium",
  Low = "Low",
}

export const ALL_PRIORITIES: Priority[] = Object.values(Priority);

export enum ValidationType {
  ProductValidation = "ProductValidation",
  ApplicationValidation = "ApplicationValidation",
  BugValidation = "BugValidation",
  AIDataValidation = "AIDataValidation",
  Other = "Other",
}

export const ALL_VALIDATION_TYPES: ValidationType[] = Object.values(ValidationType);

export const VALIDATION_TYPE_LABELS: Record<ValidationType, string> = {
  [ValidationType.ProductValidation]: "Product Validation",
  [ValidationType.ApplicationValidation]: "Application Validation",
  [ValidationType.BugValidation]: "Bug Validation",
  [ValidationType.AIDataValidation]: "AI/Data Validation",
  [ValidationType.Other]: "Other",
};

export enum ValidationResult {
  Pass = "Pass",
  Fail = "Fail",
  ConditionalPass = "ConditionalPass",
  Inconclusive = "Inconclusive",
}

export const ALL_VALIDATION_RESULTS: ValidationResult[] = Object.values(ValidationResult);

/**
 * Documents the workflow graph's edges, mirrored on the backend - the
 * Kanban board uses this to know which drags are geometrically possible.
 * `Assigned` is reachable from both `Submitted` (`accept`) and
 * `ReworkRequired` (`approveRework`); `Completed` is reachable from both
 * `InProgress` (`complete`) and `ReworkRequired` (`rejectRework`) - see
 * `kanban.constants.ts`'s `TRANSITION_ACTIONS`, which is keyed by the
 * (source, target) pair, not just the target, so it always picks the right
 * action regardless of this table's shared targets.
 */
export const VALID_TRANSITIONS: Record<RequestStatus, RequestStatus[]> = {
  [RequestStatus.Submitted]: [RequestStatus.Assigned, RequestStatus.Rejected],
  [RequestStatus.Assigned]: [RequestStatus.InProgress],
  [RequestStatus.InProgress]: [RequestStatus.Completed],
  [RequestStatus.Completed]: [RequestStatus.ReworkRequired],
  [RequestStatus.ReworkRequired]: [RequestStatus.Assigned, RequestStatus.Completed],
  [RequestStatus.Rejected]: [],
};

/**
 * `accept` folds in what used to be a separate `assign` step - a request
 * can't become Assigned without an engineer, priority and due date attached,
 * so there's no intermediate "accepted but unassigned" state. `Completed` is
 * terminal by default - there's no `close` action; a Requestor who disputes
 * the result calls `requestRework` instead, which the Validation Manager
 * then either approves (`approveRework`, re-staffs it like a fresh accept)
 * or denies (`rejectRework`, back to `Completed`).
 */
export type WorkflowAction =
  | "submit"
  | "accept"
  | "reject"
  | "start"
  | "complete"
  | "requestRework"
  | "approveRework"
  | "rejectRework";

/** Same "single source of truth" principle as VALID_TRANSITIONS - reused by
 * action buttons (which roles may see a given button) and, later, by the
 * real backend guard. */
export const ACTION_ALLOWED_ROLES: Record<WorkflowAction, TeamRole[]> = {
  submit: [TeamRole.Requestor],
  accept: [TeamRole.ValidationManager],
  reject: [TeamRole.ValidationManager],
  start: [TeamRole.ValidationEngineer],
  complete: [TeamRole.ValidationEngineer],
  requestRework: [TeamRole.Requestor],
  approveRework: [TeamRole.ValidationManager],
  rejectRework: [TeamRole.ValidationManager],
};

export interface ValidationRequest {
  id: string;
  displayId: string;
  teamId: string;
  requestorId: string;
  title: string;
  description: string;
  validationType: ValidationType;
  status: RequestStatus;
  priority: Priority | null;
  expectedCompletionDate: string | null;
  actualCompletionDate: string | null;
  extensionReason: string | null;
  validationResult: ValidationResult | null;
  summary: string | null;
  rejectionReason: string | null;
  reworkReason: string | null;
  assignedEngineerIds: string[];
  createdAt: string;
  updatedAt: string;
}

/**
 * The wizard collects every field across its 5 steps in local component
 * state and submits once, at the end - the approved spec's "Submit
 * Validation Request" is a single action producing a request that's
 * immediately `Submitted`, not a resumable multi-visit draft (that was part
 * of an earlier, superseded plan).
 */
export interface CreateRequestPayload {
  teamId: string;
  title: string;
  description: string;
  validationType: ValidationType;
}

export interface RejectPayload {
  rejectionReason: string;
}

export interface AssignPayload {
  engineerIds: string[];
  priority: Priority;
  expectedCompletionDate: string;
}

export interface CompletePayload {
  validationResult: ValidationResult;
  summary: string;
  actualCompletionDate: string;
  /** Required by the server only when actualCompletionDate is past expectedCompletionDate. */
  extensionReason?: string;
}

export interface RequestChangesPayload {
  reworkReason: string;
}

export interface Comment {
  id: string;
  requestId: string;
  authorUserId: string;
  body: string;
  createdAt: string;
}

export type AttachmentCategory = "Evidence" | "Report" | "Log" | "Screenshot" | "Video";

export const ALL_ATTACHMENT_CATEGORIES: AttachmentCategory[] = [
  "Evidence",
  "Report",
  "Log",
  "Screenshot",
  "Video",
];

export interface Attachment {
  id: string;
  requestId: string;
  uploadedByUserId: string;
  category: AttachmentCategory;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}
